/**
 * SEO utility functions for generating SEO-friendly URLs and meta data
 */

/**
 * Convert category name to SEO-friendly slug
 * @param name - Category name
 * @returns SEO-friendly slug
 */
export const generateCategorySlug = (name: string): string => {
  // Myanmar to English category name mappings
  const myanmarToEnglishMap: Record<string, string> = {
    ဖုန်းငွေဖြည့်ကဒ်: 'mobile-topup',
    ဒေတာပက်ကေ့ချ်: 'data-package',
    'ပြည်တွင်း E-PIN': 'local-epins',
    လက်ဆောင်ကဒ်: 'egift-cards',
    မိုဘိုင်းဖုန်း: 'mobile-topup',
    ဂိမ်း: 'games',
    အင်တာနက်: 'internet',
    ဘီလ်များ: 'bills',
  };

  // Check if it's a Myanmar category name
  if (myanmarToEnglishMap[name]) {
    return myanmarToEnglishMap[name];
  }

  // For English names, generate slug normally
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '') // Remove special characters except spaces and hyphens
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
    .trim()
    .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
};

/**
 * Convert slug back to category ID for matching
 * @param slug - URL slug
 * @returns Possible category ID variations
 */
export const slugToCategoryId = (slug: string): string[] => {
  const variations = [
    slug, // Direct match
    slug.replace(/-/g, ''), // Remove hyphens
    slug.replace(/-/g, ' '), // Replace hyphens with spaces
    slug.replace(/-/g, '_'), // Replace hyphens with underscores
  ];

  // Add special mappings
  const specialMappings: Record<string, string> = {
    'gift-cards': 'egift-cards',
    'e-gift-cards': 'egift-cards',
    'mobile-top-up': 'mobile-topup',
    'mobile-recharge': 'mobile-topup',
    'data-plans': 'data-package',
    'data-bundles': 'data-package',
    'local-pins': 'local-epins',
    'e-pins': 'local-epins',
  };

  if (specialMappings[slug]) {
    variations.push(specialMappings[slug]);
  }

  return variations;
};

/**
 * Generate SEO-friendly category title
 * @param categoryName - Category name
 * @param operatorName - Optional operator name
 * @returns SEO-optimized title
 */
export const generateCategoryTitle = (categoryName: string, operatorName?: string): string => {
  const baseTitle = categoryName;
  const siteName = 'My EZTopup';

  if (operatorName) {
    return `${baseTitle} for ${operatorName} | ${siteName}`;
  }

  return `${baseTitle} | ${siteName}`;
};

/**
 * Generate SEO-friendly category description
 * @param categoryName - Category name
 * @param operatorName - Optional operator name
 * @returns SEO-optimized description
 */
export const generateCategoryDescription = (
  categoryName: string,
  operatorName?: string,
): string => {
  const baseDescriptions: Record<string, string> = {
    'mobile-topup':
      'Instant mobile top-up and recharge services for all Myanmar operators. Fast, secure, and convenient digital phone credit.',
    'data-package':
      'Affordable data packages and internet bundles for all Myanmar networks. Stay connected with high-speed mobile data.',
    'local-epins':
      'Local E-PINs and digital vouchers for Myanmar. Secure electronic payment solutions for various services.',
    'egift-cards':
      'Digital gift cards and e-vouchers for Myanmar. Perfect gifts for friends and family with instant delivery.',
  };

  const categoryId = Object.keys(baseDescriptions).find(
    (id) =>
      categoryName.toLowerCase().includes(id.replace('-', ' ')) ||
      categoryName.toLowerCase().includes(id.replace('-', '')),
  );

  let description =
    baseDescriptions[categoryId || 'mobile-topup'] ||
    `${categoryName} services in Myanmar. Fast, secure, and convenient digital solutions.`;

  if (operatorName) {
    description = description
      .replace('all Myanmar operators', operatorName)
      .replace('all Myanmar networks', operatorName)
      .replace('Myanmar', `${operatorName} Myanmar`);
  }

  return description;
};

/**
 * Generate category-specific keywords
 * @param categoryName - Category name
 * @param operatorName - Optional operator name
 * @returns Comma-separated keywords
 */
export const generateCategoryKeywords = (categoryName: string, operatorName?: string): string => {
  const baseKeywords = [
    'Myanmar',
    'digital services',
    'instant delivery',
    'secure payment',
    'mobile services',
  ];

  const categoryKeywords: Record<string, string[]> = {
    'mobile-topup': [
      'mobile topup',
      'phone recharge',
      'mobile credit',
      'phone credit',
      'airtime',
      'prepaid recharge',
    ],
    'data-package': [
      'data packages',
      'internet bundles',
      'mobile data',
      'data plans',
      'internet packages',
      'mobile internet',
    ],
    'local-epins': [
      'e-pins',
      'electronic pins',
      'digital vouchers',
      'local pins',
      'payment vouchers',
    ],
    'egift-cards': [
      'gift cards',
      'e-gift cards',
      'digital gifts',
      'electronic vouchers',
      'gift vouchers',
    ],
  };

  const categoryId = Object.keys(categoryKeywords).find(
    (id) =>
      categoryName.toLowerCase().includes(id.replace('-', ' ')) ||
      categoryName.toLowerCase().includes(id.replace('-', '')),
  );

  const keywords = [...baseKeywords, ...(categoryKeywords[categoryId || 'mobile-topup'] || [])];

  if (operatorName) {
    keywords.push(operatorName, `${operatorName} Myanmar`);
  }

  // Add Myanmar operators
  keywords.push('MPT', 'Ooredoo', 'Telenor', 'Atom', 'MyTel');

  return keywords.join(', ');
};

/**
 * Generate structured data for category page
 * @param categoryName - Category name
 * @param categorySlug - Category slug
 * @param description - Category description
 * @returns JSON-LD structured data
 */
export const generateCategoryStructuredData = (
  categoryName: string,
  categorySlug: string,
  description: string,
) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: categoryName,
    description: description,
    url: `https://myeztopup.com/product/${categorySlug}`,
    mainEntity: {
      '@type': 'ItemList',
      name: `${categoryName} Products`,
      description: description,
    },
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: 'https://myeztopup.com/',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Products',
          item: 'https://myeztopup.com/product',
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: categoryName,
          item: `https://myeztopup.com/product/${categorySlug}`,
        },
      ],
    },
    provider: {
      '@type': 'Organization',
      name: 'My EZTopup',
      url: 'https://myeztopup.com/',
    },
  };
};
